import { useId, useState, type ReactElement, type ReactNode } from "react";
import { Search, X } from "lucide-react";
import { Button } from "../atoms/Button/Button.js";
import { Checkbox } from "../atoms/Checkbox/Checkbox.js";
import { RadioGroup } from "../atoms/RadioGroup/RadioGroup.js";
import { Input } from "../atoms/Input/Input.js";
import {
  AsyncSection,
  type AsyncStatus,
} from "../molecules/AsyncSection/index.js";
import { EmptyState } from "../molecules/EmptyState/index.js";

export interface CatalogPickerControlsProps {
  readonly label: string;
  readonly query?: string | undefined;
  readonly isLoading?: boolean | undefined;
  readonly isLoadingMore?: boolean | undefined;
  readonly error?: string | null | undefined;
  readonly hasMore?: boolean | undefined;
  readonly isEmpty: boolean;
  readonly onSearchChange?: ((query: string) => void) | undefined;
  readonly onLoadMore?: (() => void) | undefined;
  readonly onRetry?: (() => void) | undefined;
  /** Optional controlled selection, sharing search / result / pagination order. */
  readonly items?: readonly { id: string; label: string; detail?: ReactNode }[];
  readonly selectedIds?: readonly string[];
  readonly onSelectionChange?: ((ids: string[]) => void) | undefined;
  readonly multiple?: boolean;
  readonly maxSelected?: number;
  /** Large scope catalogues only show results after a search. */
  readonly requireSearch?: boolean;
  readonly bulkSelection?: boolean;
  readonly children?: ReactNode;
}

/** Data-blind catalogue search. Pins survive paging, searches and unavailable items. */
export function CatalogPickerControls({
  label,
  query: hostQuery,
  isLoading,
  isLoadingMore,
  error,
  hasMore,
  isEmpty,
  onSearchChange,
  onLoadMore,
  onRetry,
  items,
  selectedIds: rawSelectedIds = [],
  onSelectionChange,
  multiple = true,
  maxSelected,
  requireSearch = false,
  bulkSelection = false,
  children,
}: CatalogPickerControlsProps): ReactElement {
  const selectedIds = [...new Set(rawSelectedIds)];
  const [localQuery, setLocalQuery] = useState("");
  const query = onSearchChange ? (hostQuery ?? "") : localQuery;
  const search = onSearchChange ?? setLocalQuery;
  const radioName = useId();
  // A label cache only, never a source of selectable choices or authorization.
  const [cache, setCache] = useState(() => ({
    items,
    labels: new Map((items ?? []).map((item) => [item.id, item])),
  }));
  if (cache.items !== items)
    setCache({
      items,
      labels: new Map([
        ...cache.labels,
        ...(items ?? []).map((item) => [item.id, item] as const),
      ]),
    });
  const labels = new Map([
    ...cache.labels,
    ...(items ?? []).map((item) => [item.id, item] as const),
  ]);
  const q = query.trim().toLocaleLowerCase();
  const matches = (items ?? []).filter((item) =>
    item.label.toLocaleLowerCase().includes(q),
  );
  const results = requireSearch && !q ? [] : q ? matches : matches.slice(0, 5);
  const status: AsyncStatus = error
    ? "error"
    : isLoading
      ? "loading"
      : (items ? results.length === 0 : isEmpty)
        ? "empty"
        : "ready";
  const toggle = (id: string): void => {
    const selected = selectedIds.includes(id);
    onSelectionChange?.(
      selected
        ? selectedIds.filter((value) => value !== id)
        : multiple
          ? [...selectedIds, id]
          : [id],
    );
  };
  return (
    <div className="space-y-3">
      {onSearchChange || items ? (
        <Input
          type="search"
          aria-label={`Search ${label}`}
          value={query}
          onChange={(event) => search(event.target.value)}
          placeholder={`Search ${label}`}
          prefix={<Search aria-hidden className="h-3.5 w-3.5" />}
        />
      ) : null}
      {items && selectedIds.length > 0 ? (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <p className="text-dense-1 font-medium text-pb-text-secondary">
              Selected ({selectedIds.length})
            </p>
            {multiple && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onSelectionChange?.([])}
              >
                Clear selection
              </Button>
            )}
          </div>
          <div className="max-h-40 overflow-y-auto rounded-md border border-pb-border divide-y divide-pb-border">
            {selectedIds.map((id) => {
              const item = labels.get(id);
              return (
                <div
                  key={id}
                  className="flex items-center justify-between gap-2 px-3 py-2"
                >
                  <span className="min-w-0 text-sm text-pb-text break-words">
                    {item?.label ??
                      `Unavailable ${label === "KPIs" ? "KPI" : "selection"}`}
                    {item?.detail && (
                      <span className="block text-dense-1 text-pb-text-muted">
                        {item.detail}
                      </span>
                    )}
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={`Remove ${item?.label ?? "unavailable selection"}`}
                    onClick={() =>
                      onSelectionChange?.(
                        selectedIds.filter((value) => value !== id),
                      )
                    }
                  >
                    <X aria-hidden className="h-3.5 w-3.5" />
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
      {requireSearch && !q && !error && !isLoading ? (
        <p className="text-dense-1 text-pb-text-muted">
          Search by name to choose {label}.
        </p>
      ) : status !== "ready" ? (
        <AsyncSection
          status={status}
          size="sm"
          errorTitle={error ?? ""}
          loadingLabel={label}
          {...(onRetry ? { onRetry } : {})}
          skeleton={
            <p className="text-dense-1 text-pb-text-muted">Loading {label}…</p>
          }
          empty={
            <EmptyState
              size="sm"
              variant="no-results"
              title={`No matching ${label}.`}
            />
          }
        >
          {null}
        </AsyncSection>
      ) : null}
      {items && status === "ready" && (!requireSearch || q) ? (
        <div className="space-y-2">
          {bulkSelection && results.length > 0 && (
            <Button
              size="sm"
              variant="ghost"
              disabled={
                maxSelected !== undefined &&
                new Set([...selectedIds, ...results.map((item) => item.id)])
                  .size > maxSelected
              }
              onClick={() =>
                onSelectionChange?.([
                  ...new Set([
                    ...selectedIds,
                    ...results.map((item) => item.id),
                  ]),
                ])
              }
            >
              Select these {results.length} results
            </Button>
          )}
          <div className="grid gap-3 max-h-56 overflow-y-auto rounded-md border border-pb-border p-3">
            {multiple ? (
              results.map((item) => (
                <Checkbox
                  key={item.id}
                  checked={selectedIds.includes(item.id)}
                  disabled={
                    !selectedIds.includes(item.id) &&
                    maxSelected !== undefined &&
                    selectedIds.length >= maxSelected
                  }
                  onChange={() => toggle(item.id)}
                  label={
                    <span className="flex flex-wrap items-center gap-x-2">
                      <span className="break-words">{item.label}</span>
                      <span className="text-dense-1 text-pb-text-muted">
                        {item.detail}
                      </span>
                    </span>
                  }
                />
              ))
            ) : (
              <RadioGroup
                name={radioName}
                aria-label={label}
                value={selectedIds[0] ?? ""}
                onChange={(id) => onSelectionChange?.([id])}
                options={results.map((item) => ({
                  value: item.id,
                  label: item.label,
                  trailing: item.detail,
                }))}
              />
            )}
          </div>
          {!q && matches.length > results.length && (
            <p className="text-dense-1 text-pb-text-muted">
              Search to find more {label}.
            </p>
          )}
        </div>
      ) : null}
      {children}
      {hasMore && onLoadMore && (!items || Boolean(q)) ? (
        <Button
          className="w-full"
          size="sm"
          loading={Boolean(isLoadingMore)}
          onClick={onLoadMore}
        >
          Load more
        </Button>
      ) : null}
      {maxSelected !== undefined && selectedIds.length >= maxSelected && (
        <p className="text-dense-1 text-pb-text-muted">
          You can select up to {maxSelected} {label}.
        </p>
      )}
    </div>
  );
}
